import type { FC, ReactNode } from 'react'

import { Ticket } from 'lucide-react'
import { cn } from '@/lib/utils'
import { invitation } from '@/constants/invitation'

const BARCODE = ['w-0.5', 'w-px', 'w-[3px]', 'w-px', 'w-0.5', 'w-px', 'w-px', 'w-[3px]', 'w-0.5', 'w-px', 'w-0.5', 'w-[3px]', 'w-px', 'w-0.5']

const InvitationTicket: FC = (): ReactNode => {
    const { honoree, age, date, time, venue, dressCode } = invitation

    return (
        <div className='relative mx-auto w-full max-w-sm animate-in fade-in zoom-in-95 delay-150 duration-700 fill-mode-both ease-out motion-reduce:animate-none sm:max-w-md lg:max-w-none'>
            <div className='-rotate-3 drop-shadow-2xl drop-shadow-foreground/15 transition-[rotate] duration-500 ease-out hover:rotate-0 motion-reduce:transition-none'>
                <div className='ticket-top overflow-hidden rounded-t-[2rem] bg-card'>
                    <div className='flex items-center justify-between bg-foreground px-7 py-4 text-background'>
                        <span className='text-xs font-bold uppercase tracking-[0.3em]'>
                            Invitation
                        </span>

                        <span className='inline-flex items-center gap-1.5 text-xs font-semibold text-background/70'>
                            <Ticket className='size-4' aria-hidden='true' />
                            Entrée VIP
                        </span>
                    </div>

                    <div className='px-7 pt-7 pb-9'>
                        <p className='text-sm font-medium text-muted-foreground'>
                            Soirée d’anniversaire de
                        </p>

                        <p className='mt-1 font-heading text-5xl leading-none tracking-tight break-words sm:text-6xl'>
                            {honoree}
                        </p>

                        <div className='mt-7 flex items-center gap-5'>
                            <div className='grid size-20 shrink-0 place-items-center rounded-2xl bg-secondary text-center'>
                                <div>
                                    <p className='text-[0.65rem] font-bold uppercase tracking-widest text-primary'>
                                        {date.weekdayShort}
                                    </p>

                                    <p className='font-heading text-4xl leading-none'>
                                        {date.day}
                                    </p>

                                    <p className='text-[0.65rem] font-bold uppercase tracking-widest text-muted-foreground'>
                                        {date.monthShort}
                                    </p>
                                </div>
                            </div>

                            <ul className='grid min-w-0 gap-3 text-sm'>
                                <li>
                                    <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                        Horaires
                                    </p>

                                    <p className='mt-0.5 font-semibold'>
                                        {time.start} – {time.end}
                                    </p>
                                </li>

                                <li>
                                    <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                        Lieu
                                    </p>

                                    <p className='mt-0.5 font-semibold'>
                                        {venue.name}
                                    </p>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className='ticket-stub rounded-b-[2rem] bg-card'>
                    <div aria-hidden='true' className='mx-6 border-t-2 border-dashed border-border' />

                    <div className='flex items-center justify-between gap-4 px-7 pt-5 pb-6'>
                        <div className='min-w-0'>
                            <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                Dress code
                            </p>

                            <p className='mt-0.5 font-semibold'>
                                {dressCode.title}
                            </p>
                        </div>

                        <div aria-hidden='true' className='flex h-10 shrink-0 items-stretch gap-[3px]'>
                            {BARCODE.map((width, index) => (
                                <span key={index} className={cn('rounded-[1px] bg-foreground/80', width)} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div
                aria-hidden='true'
                className='absolute -top-8 -right-2 grid size-24 rotate-12 place-items-center rounded-full bg-accent text-center text-accent-foreground shadow-lg ring-4 ring-background sm:-right-6 sm:size-28'
            >
                <div>
                    <p className='font-heading text-4xl leading-none sm:text-5xl'>
                        {age}
                    </p>

                    <p className='text-[0.65rem] font-bold uppercase tracking-[0.25em]'>
                        ans
                    </p>
                </div>
            </div>
        </div>
    )
}

export default InvitationTicket
